import React, { useContext, useEffect, useState } from 'react'
import { Pattern } from '../contexts/PatternContext'
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
} from '@mui/material'
import { alpha, styled, useTheme } from '@mui/material/styles'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationAddIcon from '@mui/icons-material/NotificationAdd'
import AssessmentIcon from '@mui/icons-material/Assessment'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import ContentCopy from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ReplyIcon from '@mui/icons-material/Reply'
import StandardBadge from '../../../components/StandardBadge'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  getDoc,
  query,
  updateDoc,
  where,
} from 'firebase/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import { deletePattern } from '../../../common/utils/deletePattern'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { useSavePattern } from '../../../hooks/useSavePattern'
import { useCopyPatternWithSave } from '../../../hooks/useCopyPatternToAccount'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import ShareDialog from './sidebar/patternInput/ShareDialog'
import generateShareLink from '../../../common/utils/generateShareLink'
import ReactGA from 'react-ga4'
import _cloneDeep from 'lodash.clonedeep'
import { useModalContext } from '../../../contexts/ModalProvider'
import { defaultInputSettings } from '../../../contexts/ApplicationSuiteProvider'
import { useBacktestContext } from '../../backtest/contexts/BacktestContext'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import LockIcon from '@mui/icons-material/Lock'
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings'
import UpgradeDialog from '../../../components/UpgradeDialog'
import { useCallableRef } from '../../../api/useCallableRef'
import { COMPOSITE_GATE_MESSAGE, compositeGateActive } from '../../../common/utils/composite'

// Card-surface row matching MyIndicatorsList's IndicatorRow
const StyledRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'starred',
})<{ starred?: boolean }>(({ theme, starred }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.background.custom2,
  border: `1px solid ${
    starred ? alpha(theme.palette.warning.main, 0.4) : theme.palette.background.custom4
  }`,
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  ...(starred && {
    boxShadow: `0 0 12px ${alpha(theme.palette.warning.main, 0.08)}`,
  }),
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.5),
    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}))

const countChipSx = {
  height: 22,
  flexShrink: 0,
  '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
}

interface PatternRowProps {
  pattern: Pattern
  isTradingHousePattern?: boolean
  onEdit: (pattern: Pattern) => void
}

function PatternRow({ pattern, isTradingHousePattern, onEdit }: PatternRowProps) {
  const theme = useTheme()
  const { usermeta, tradingHouse, canViewPrivatePatterns, canCreateAlerts } =
    useApplicationSuiteContext()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const { setIsCreateAlertModalOpen, setDefaultAlert, setIsBacktestRunDialogOpen } =
    useModalContext()
  const { setBacktestSettingInputData } = useBacktestContext()

  const [alertCount, setAlertCount] = useState(0)
  const [globalAlertCount, setGlobalAlertCount] = useState(0)
  const [globalAutoUpdateCount, setGlobalAutoUpdateCount] = useState(0)
  const [backtestCount, setBacktestCount] = useState(0)
  const [createdAt, setCreatedAt] = useState<number | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [duplicateName, setDuplicateName] = useState('')
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [shareable, setShareable] = useState<any>(null)
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false)

  const { forceSave } = useSavePattern(() => {}, usermeta, tradingHouse, canViewPrivatePatterns)
  const { copyPattern } = useCopyPatternWithSave(forceSave)

  const [getPatternAlertsCountCallable] = useCallableRef('getPatternAlertsCount')

  useEffect(() => {
    if (!pattern?.id) {
      return
    }
    if (!isTradingHousePattern) {
      getAlertsCount(pattern.id)
      getBacktestsCount(pattern.id)
      getCreatedAt(pattern.id)
    }
    if (usermeta.isAdmin) {
      getGlobalAlertsCount(pattern.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern?.id, isTradingHousePattern, usermeta.isAdmin])

  const getAlertsCount = async (patternId: string) => {
    try {
      const alertsRef = collection(firebase_firestore, 'tradingPlans')
      const q = query(
        alertsRef,
        where('pattern.id', '==', patternId),
        where('user.gcid', '==', usermeta.gcid)
      )
      const snapshot = await getDocs(q)
      const count = snapshot.docs.reduce((acc, docSnap) => {
        const data = docSnap.data() as { alertTypes?: unknown }
        const alertTypes = Array.isArray(data?.alertTypes) ? (data.alertTypes as unknown[]) : []
        const hasExitTradingPlan = alertTypes.includes('exitTradingPlan')
        return hasExitTradingPlan ? acc : acc + 1
      }, 0)
      setAlertCount(count)
    } catch (error) {
      console.error('Error getting alerts count:', error)
      setAlertCount(0)
    }
  }

  const getGlobalAlertsCount = async (patternId: string) => {
    try {
      const result: any = await getPatternAlertsCountCallable({ patternId })
      if (result?.data?.status !== 200) {
        setGlobalAlertCount(0)
        setGlobalAutoUpdateCount(0)
        return
      }
      setGlobalAlertCount(typeof result.data.count === 'number' ? result.data.count : 0)
      setGlobalAutoUpdateCount(
        typeof result.data.autoUpdateCount === 'number' ? result.data.autoUpdateCount : 0
      )
    } catch (error) {
      console.error('Error getting global alerts count:', error)
      setGlobalAlertCount(0)
      setGlobalAutoUpdateCount(0)
    }
  }

  const getBacktestsCount = async (patternId: string) => {
    try {
      const backtestsRef = collection(firebase_firestore, `usermeta/${usermeta.gcid}/backtests`)
      const q = query(backtestsRef, where('pattern.id', '==', patternId))
      const snapshot = await getCountFromServer(q)
      setBacktestCount(snapshot.data().count)
    } catch (error) {
      console.error('Error getting backtests count:', error)
      setBacktestCount(0)
    }
  }

  const getCreatedAt = async (patternId: string) => {
    try {
      const versionDoc = await getDoc(
        doc(firebase_firestore, `usermeta/${usermeta.gcid}/patterns/${patternId}/versions/1`)
      )
      if (versionDoc.exists()) {
        setCreatedAt(versionDoc.data().created ?? null)
      }
    } catch (error) {
      console.error('Error getting pattern createdAt:', error)
    }
  }

  const handleRowClick = () => {
    onEdit(pattern)
  }

  const handleToggleStar = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!pattern.id || isTradingHousePattern) return
    try {
      const patternRef = doc(firebase_firestore, `usermeta/${usermeta.gcid}/patterns/${pattern.id}`)
      await updateDoc(patternRef, { starred: !pattern.starred })
    } catch (error) {
      console.error('Error toggling star:', error)
    }
  }

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleCopyPattern = async () => {
    handleMenuClose()
    if (!pattern) return
    await copyPattern(pattern)
  }

  const handleDuplicatePattern = async () => {
    if (!pattern) return
    try {
      setSnackbarSettings({
        message: 'Duplicating pattern...',
        severity: 'info',
        autoHideDuration: 3000,
      })
      const patternCopy = JSON.parse(JSON.stringify(pattern))
      delete patternCopy.id
      patternCopy.name = duplicateName || `${pattern.name} (Copy)`
      patternCopy.version = undefined
      patternCopy.alertVersion = undefined
      const savedPattern = await forceSave(patternCopy)
      if (savedPattern) {
        ReactGA.event('pattern_duplicate', { pattern_name: patternCopy.name })
        setSnackbarSettings({
          message: 'Pattern duplicated successfully!',
          severity: 'success',
          autoHideDuration: 3000,
        })
      }
    } catch (error) {
      console.error('Error duplicating pattern:', error)
      setSnackbarSettings({
        message: 'Error duplicating pattern',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }
    setIsDuplicateDialogOpen(false)
  }

  const handleDeletePattern = async () => {
    if (!pattern) return
    try {
      await deletePattern(pattern, isTradingHousePattern, usermeta)
      setSnackbarSettings({
        message: 'Pattern deleted successfully!',
        severity: 'success',
        autoHideDuration: 3000,
      })
    } catch (error) {
      console.error('Error deleting pattern:', error)
      setSnackbarSettings({
        message: 'Error deleting pattern',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }
    setDeleteDialogOpen(false)
  }

  const handleShare = async () => {
    handleMenuClose()
    if (!pattern || !pattern.id) return
    try {
      const link = await generateShareLink(
        pattern.id,
        `usermeta/${usermeta.gcid}/patterns/${pattern.id}`,
        'PATTERN',
        { name: pattern.name, createdBy: { gcid: usermeta?.gcid, email: usermeta?.email } }
      )
      setShareable(link)
      setIsShareDialogOpen(true)
    } catch (error) {
      console.error('Error generating share link:', error)
    }
  }

  const handleCreateAlert = () => {
    handleMenuClose()
    if (!pattern) return
    if (compositeGateActive(pattern)) {
      setSnackbarSettings({ severity: 'info', message: COMPOSITE_GATE_MESSAGE })
      return
    }
    const _pattern = _cloneDeep(pattern)
    setDefaultAlert((prev: any) => ({
      ...prev,
      pattern: _pattern,
      name: `${_pattern?.name || 'Unnamed Pattern'} - Alert`,
      from: 'pattern',
    }))
    setIsCreateAlertModalOpen(true)
  }

  const handleRunBacktest = () => {
    handleMenuClose()
    if (!pattern) return
    if (compositeGateActive(pattern)) {
      setSnackbarSettings({ severity: 'info', message: COMPOSITE_GATE_MESSAGE })
      return
    }
    setBacktestSettingInputData({
      ...defaultInputSettings,
      patternId: pattern.id,
    })
    setIsBacktestRunDialogOpen(true)
  }

  const triggerLabel = pattern.trigger?.chartDetails
    ? `${pattern.trigger.chartDetails.periodicity}${pattern.trigger.chartDetails.timeframe === 'minute' ? 'min' : pattern.trigger.chartDetails.timeframe === 'hour' ? 'hr' : pattern.trigger.chartDetails.timeframe}`
    : 'No trigger'

  const triggerType =
    pattern.trigger?.type === 'atPost'
      ? 'At Post'
      : pattern.trigger?.type === 'anytime'
        ? 'Anytime'
        : pattern.trigger?.type === 'timeBeforePost'
          ? `${(pattern.trigger as any).value || ''}${(pattern.trigger as any).timeframe === 'second' ? 's' : (pattern.trigger as any).timeframe === 'minute' ? 'min' : (pattern.trigger as any).timeframe === 'hour' ? 'hr' : ''} Before Post`
          : ''

  const createdLabel =
    !isTradingHousePattern && createdAt
      ? `Created ${new Date(createdAt).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}`
      : ''

  const caption = [triggerLabel, triggerType, createdLabel].filter(Boolean).join(' · ')

  return (
    <>
      <StyledRow
        className="pattern-card"
        starred={!isTradingHousePattern && pattern.starred}
        onClick={handleRowClick}
        data-tour-id={isTradingHousePattern ? 'trading-house-pattern' : 'user-pattern'}
        data-pattern-id={pattern.id}
      >
        {!isTradingHousePattern && (
          <Tooltip title={pattern.starred ? 'Unfavorite' : 'Favorite'}>
            <IconButton
              size="small"
              onClick={handleToggleStar}
              sx={{
                flexShrink: 0,
                opacity: pattern.starred ? 1 : 0.3,
                '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              {pattern.starred ? (
                <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
              ) : (
                <StarBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="subtitle1"
              data-tour-id="pattern-name"
              sx={{
                color: 'common.white',
                fontWeight: 600,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {pattern.name || 'Unnamed Pattern'}
            </Typography>
            {(pattern as any).source === 'strategy-agent' && (
              <Tooltip title="Created by AI Session">
                <SmartToyOutlinedIcon
                  sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }}
                />
              </Tooltip>
            )}
            {isTradingHousePattern && <StandardBadge label="Standard" />}
            {!isTradingHousePattern && alertCount > 0 && (
              <Tooltip title={`${alertCount} alert${alertCount !== 1 ? 's' : ''}`}>
                <Chip
                  icon={<NotificationsActiveIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label={alertCount}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={countChipSx}
                />
              </Tooltip>
            )}
            {usermeta.isAdmin && globalAlertCount > 0 && (
              <Tooltip
                title={`${globalAutoUpdateCount} of ${globalAlertCount} alert${globalAlertCount !== 1 ? 's' : ''} across all users ${globalAlertCount !== 1 ? 'are' : 'is'} set to auto-update`}
              >
                <Chip
                  icon={<AdminPanelSettings sx={{ fontSize: '0.85rem !important' }} />}
                  label={`${globalAutoUpdateCount}/${globalAlertCount}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={countChipSx}
                />
              </Tooltip>
            )}
            {!isTradingHousePattern && backtestCount > 0 && (
              <Tooltip title={`${backtestCount} backtest${backtestCount !== 1 ? 's' : ''}`}>
                <Chip
                  icon={<AssessmentIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label={backtestCount}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={countChipSx}
                />
              </Tooltip>
            )}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {caption}
          </Typography>
        </Box>

        {isTradingHousePattern ? (
          <Tooltip title="Copy to Account">
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleCopyPattern()
              }}
              endIcon={<FileCopyIcon sx={{ fontSize: '0.85rem !important' }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: theme.palette.primary.main,
                minWidth: 0,
                px: 1,
                flexShrink: 0,
              }}
              data-tour-id="pattern-copy-button"
            >
              Copy
            </Button>
          </Tooltip>
        ) : (
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              flexShrink: 0,
              opacity: 0.5,
              '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.06)' },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </StyledRow>

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={
            canCreateAlerts
              ? handleCreateAlert
              : () => {
                  setMenuAnchor(null)
                  setIsUpgradeDialogOpen(true)
                }
          }
        >
          {canCreateAlerts ? (
            <NotificationAddIcon fontSize="small" sx={{ mr: 1 }} />
          ) : (
            <LockIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
          )}
          Create Alert
        </MenuItem>
        <MenuItem onClick={handleRunBacktest}>
          <PlayArrowIcon fontSize="small" sx={{ mr: 1 }} />
          Run Backtest
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose()
            setDuplicateName(`${pattern?.name || 'Pattern'} (Copy)`)
            setIsDuplicateDialogOpen(true)
          }}
        >
          <ContentCopy fontSize="small" sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        <MenuItem onClick={handleShare}>
          <ReplyIcon sx={{ transform: 'scaleX(-1)', mr: 1 }} fontSize="small" />
          Share
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose()
            setDeleteDialogOpen(true)
          }}
          sx={{ color: theme.palette.error.main }}
        >
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Pattern Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this pattern? All alerts and backtests associated with
            this pattern will still be available.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary" variant="contained">
            Cancel
          </Button>
          <Button onClick={handleDeletePattern} color="error" autoFocus variant="outlined">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={isDuplicateDialogOpen} onClose={() => setIsDuplicateDialogOpen(false)}>
        <DialogTitle>Duplicate Pattern</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a copy of this pattern with a new name. The duplicate will start at version 1
            with a clean version history.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Pattern Name"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && duplicateName.trim()) {
                handleDuplicatePattern()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDuplicateDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDuplicatePattern}
            color="primary"
            variant="contained"
            disabled={!duplicateName.trim()}
            endIcon={<ContentCopy fontSize="small" />}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <ShareDialog
        open={isShareDialogOpen}
        title="Share this pattern"
        onClose={() => setIsShareDialogOpen(false)}
        shareable={shareable}
      />

      {/* Upgrade Dialog */}
      <UpgradeDialog open={isUpgradeDialogOpen} onClose={() => setIsUpgradeDialogOpen(false)} />
    </>
  )
}

export default PatternRow
